import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Typography,
  Box,
  Alert,
  Chip,
  Tooltip,
  Container,
  Button,
  Grid
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterListIcon from '@mui/icons-material/FilterList';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import '../styles/pdfExport.css';

const TablaTenido = () => {
  console.log('🔵 TablaTenido component mounted');
  const [asignacionesFiltradas, setAsignacionesFiltradas] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [notas, setNotas] = useState({});
  
  // Inicializar con la fecha actual
  const hoy = format(new Date(), 'yyyy-MM-dd');
  const [fechaInicio, setFechaInicio] = useState(hoy);
  const [fechaFin, setFechaFin] = useState(hoy);

  const fetchAsignaciones = async (inicio = fechaInicio, fin = fechaFin) => {
    console.log('🔵 fetchAsignaciones called con fechas:', inicio, fin);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Debes iniciar sesión para ver las asignaciones');
        return;
      }
      
      const headers = { 'Authorization': `Bearer ${token}` };
      // Usar el endpoint de filtrado con fechas
      const url = `/api/asignar-tenido/filtrar?fechaInicio=${inicio}&fechaFin=${fin}`;
      const response = await fetch(url, { headers });
      
      if (!response.ok) {
        if (response.status === 401) {
          setError('Sesión expirada. Por favor, inicia sesión nuevamente');
        } else {
          setError(`Error ${response.status}: ${response.statusText}`);
        }
        return;
      }
      
      const data = await response.json();
      console.log('📊 Datos recibidos:', data.length, 'asignaciones');
      
      if (Array.isArray(data)) {
        setAsignacionesFiltradas(data);
        
        // Inicializar las notas
        const notasIniciales = {};
        data.forEach(asig => {
          notasIniciales[asig._id] = asig.nota || '';
        });
        setNotas(notasIniciales);
      } else {
        console.warn('Los datos no son un array:', data);
        setError(data.message || 'Formato de datos inválido');
      }
    } catch (err) {
      console.error('Error al cargar asignaciones:', err);
      setError('Error al cargar las asignaciones: ' + err.message);
    }
  };

  useEffect(() => {
    fetchAsignaciones();
  }, []);

  const handleNotaChange = (id, value) => {
    setNotas({ ...notas, [id]: value });
  };

  const handleGuardarNota = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };
      
      const response = await fetch(`/api/asignar-tenido/${id}/nota`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ nota: notas[id] })
      });

      if (response.ok) {
        setSuccess('Nota guardada correctamente');
        setTimeout(() => setSuccess(null), 3000);
        fetchAsignaciones(fechaInicio, fechaFin);
      } else {
        setError('Error al guardar la nota');
      }
    } catch (err) {
      setError('Error de conexión al servidor');
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta asignación?')) return;

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`/api/asignar-tenido/${id}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        setSuccess('Asignación eliminada correctamente');
        setTimeout(() => setSuccess(null), 3000);
        fetchAsignaciones(fechaInicio, fechaFin);
      } else {
        setError('Error al eliminar la asignación');
      }
    } catch (err) {
      setError('Error de conexión al servidor');
    }
  };

  const handleFiltrar = () => {
    if (!fechaInicio || !fechaFin) {
      alert('Debes seleccionar ambas fechas para filtrar');
      return;
    }
    if (fechaInicio > fechaFin) {
      alert('La fecha de inicio debe ser menor o igual a la fecha final');
      return;
    }
    // Recargar datos desde el backend con las fechas seleccionadas
    fetchAsignaciones(fechaInicio, fechaFin);
  };

  const handleLimpiarFiltros = () => {
    const hoy = format(new Date(), 'yyyy-MM-dd');
    setFechaInicio(hoy);
    setFechaFin(hoy);
    fetchAsignaciones(hoy, hoy);
  };

  const descargarPDF = () => {
    const input = document.getElementById('tabla-tenido');
    if (!input) return;
    input.classList.add('pdf-export');
    
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const marginX = 10;
      const marginY = 15;
      const imgWidth = pdfWidth - (marginX * 2);
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
      const pageContentHeight = pdfHeight - (marginY * 2);
      
      let heightLeft = imgHeight;
      let position = marginY;
      
      pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
      heightLeft -= pageContentHeight;
      
      while (heightLeft > 0) {
        position = -(imgHeight - heightLeft) + marginY;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
        heightLeft -= pageContentHeight;
      }
      
      pdf.save('tabla-tenido.pdf');
      input.classList.remove('pdf-export');
    });
  };

  // Agrupar por fecha
  const agruparPorFecha = () => {
    const grupos = {};
    const timeZone = 'America/Bogota';
    asignacionesFiltradas.forEach(asig => {
      // Convertir la fecha UTC a zona horaria de Bogotá
      const fechaUTC = parseISO(asig.fechaInicio);
      const fechaBogota = toZonedTime(fechaUTC, timeZone);
      const fecha = format(fechaBogota, 'yyyy-MM-dd');
      if (!grupos[fecha]) {
        grupos[fecha] = [];
      }
      grupos[fecha].push(asig);
    });
    return grupos;
  };

  // Calcular totales por máquina
  const calcularTotalesPorMaquina = () => {
    const totales = {};
    asignacionesFiltradas.forEach(asig => {
      if (!totales[asig.machineNumber]) {
        totales[asig.machineNumber] = 0;
      }
      totales[asig.machineNumber] += asig.cantidad;
    });
    return totales;
  };

  // Calcular totales por fecha
  const calcularTotalesPorFecha = (asignacionesFecha) => {
    return asignacionesFecha.reduce((sum, asig) => sum + asig.cantidad, 0);
  };

  const gruposPorFecha = agruparPorFecha();
  const totalesPorMaquina = calcularTotalesPorMaquina();
  const totalGeneral = asignacionesFiltradas.reduce((sum, asig) => sum + asig.cantidad, 0);

  // Obtener máquinas únicas
  const maquinasUnicas = [...new Set(asignacionesFiltradas.map(a => a.machineNumber))].sort();

  return (
    <Container 
      maxWidth={false}
      sx={{ 
        width: { xs: '100%', xl: '90%' },
        p: 3,
        fontFamily: 'Roboto',
        fontWeight: 200
      }}
    >
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          mb: 3, 
          color: '#fff',
          fontFamily: 'Roboto',
          fontWeight: 300
        }}
      >
        Tabla de Teñido
      </Typography>

      {/* Formulario de filtrado */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#2a2a2a' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Fecha Inicio"
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                '& .MuiInputBase-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Fecha Fin"
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                '& .MuiInputBase-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={handleFiltrar}
                fullWidth
                sx={{ 
                  backgroundColor: '#4caf50',
                  fontFamily: 'Roboto',
                  fontWeight: 300,
                  color: '#0a0a0aff',
                  '&:hover': { backgroundColor: '#45a049' }
                }}
              >
                Filtrar
              </Button>
              <Button
                variant="outlined"
                onClick={handleLimpiarFiltros}
                sx={{ 
                  color: '#fff',
                  borderColor: '#555',
                  fontFamily: 'Roboto',
                  fontWeight: 200,
                  '&:hover': { borderColor: '#777' }
                }}
              >
                Limpiar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Botón de descarga PDF */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          onClick={descargarPDF}
          sx={{ 
            backgroundColor: '#d32f2f',
            color: '#080808ff',
            fontFamily: 'Roboto',
            fontWeight: 300,
            '&:hover': { backgroundColor: '#b71c1c' }
          }}
        >
          Descargar PDF
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, fontFamily: 'Roboto', fontWeight: 200 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, fontFamily: 'Roboto', fontWeight: 200 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {asignacionesFiltradas.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2, fontFamily: 'Roboto', fontWeight: 200 }}>
          No hay asignaciones de teñido en el rango seleccionado.
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#2a2a2a' }} id="tabla-tenido">
          <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1a1a1a' }}>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Fecha/Hora</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Referencia</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Máquina</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Color</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Código</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }} align="right">Cantidad (kg)</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Comentarios</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(gruposPorFecha).sort().reverse().map(fecha => (
              <React.Fragment key={fecha}>
                {gruposPorFecha[fecha].map((asig, index) => (
                  <TableRow 
                    key={asig._id}
                    sx={{ 
                      '&:hover': { backgroundColor: '#3a3a3a' },
                      backgroundColor: index % 2 === 0 ? '#2a2a2a' : '#323232'
                    }}
                  >
                    <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}>
                      {format(toZonedTime(parseISO(asig.fechaInicio), 'America/Bogota'), "dd/MM/yyyy HH:mm", { locale: es })}
                      {' - '}
                      {format(toZonedTime(parseISO(asig.fechaFin), 'America/Bogota'), "HH:mm", { locale: es })}
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}>{asig.referencia}</TableCell>
                    <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}>
                      <Chip 
                        label={asig.machineNumber} 
                        size="small" 
                        sx={{ backgroundColor: '#555', color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          width: 40,
                          height: 25,
                          backgroundColor: asig.color,
                          borderRadius: 1,
                          border: '1px solid #666'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}>
                      {asig.color}
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }} align="right">
                      {asig.cantidad.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={notas[asig._id] || ''}
                        onChange={(e) => handleNotaChange(asig._id, e.target.value)}
                        placeholder="Agregar comentario..."
                        size="small"
                        fullWidth
                        multiline
                        maxRows={3}
                        sx={{
                          '& .MuiInputBase-root': {
                            color: '#fff',
                            backgroundColor: '#1a1a1a',
                            fontFamily: 'Roboto',
                            fontWeight: 200
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#555'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Guardar nota">
                        <IconButton
                          onClick={() => handleGuardarNota(asig._id)}
                          sx={{ color: '#4caf50' }}
                          size="small"
                        >
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          onClick={() => handleEliminar(asig._id)}
                          sx={{ color: '#f44336' }}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Fila de total por fecha */}
                <TableRow sx={{ backgroundColor: '#1a1a1a' }}>
                  <TableCell colSpan={5} sx={{ color: '#ffd600', fontFamily: 'Roboto', fontWeight: 300 }}>
                    Total {format(parseISO(fecha), "dd/MM/yyyy", { locale: es })}
                  </TableCell>
                  <TableCell sx={{ color: '#ffd600', fontFamily: 'Roboto', fontWeight: 300 }} align="right">
                    {calcularTotalesPorFecha(gruposPorFecha[fecha]).toFixed(2)} kg
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </React.Fragment>
            ))}
            
            {/* Fila de totales por máquina */}
            <TableRow sx={{ backgroundColor: '#0a0a0a' }}>
              <TableCell colSpan={8} sx={{ py: 0 }} />
            </TableRow>
            <TableRow sx={{ backgroundColor: '#1a1a1a' }}>
              <TableCell colSpan={2} sx={{ color: '#ffd600', fontFamily: 'Roboto', fontWeight: 300, fontSize: '1.1em' }}>
                TOTALES POR MÁQUINA
              </TableCell>
              <TableCell colSpan={6} />
            </TableRow>
            {maquinasUnicas.map(maquina => (
              <TableRow key={`total-${maquina}`} sx={{ backgroundColor: '#2a2a2a' }}>
                <TableCell colSpan={2} sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}>Máquina {maquina}</TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell sx={{ color: '#4caf50', fontFamily: 'Roboto', fontWeight: 300 }} align="right">
                  {totalesPorMaquina[maquina]?.toFixed(2)} kg
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            ))}
            
            {/* Total general */}
            <TableRow sx={{ backgroundColor: '#050b13ff' }}>
              <TableCell colSpan={5} sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300, fontSize: '1.2em' }}>
                TOTAL GENERAL
              </TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300, fontSize: '1.2em' }} align="right">
                {totalGeneral.toFixed(2)} kg
              </TableCell>
              <TableCell colSpan={2} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </Container>
  );
};

export default TablaTenido;
